/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

// Pure HTML-generation helpers for the print view. These build the markup strings only; all window /
// tab management and DOM writes stay in the caller (MonsterContainer.handleOpenPrintView).

// Collect the current page's stylesheets into <style> strings (skips body rules, mirrored below).
export function collectStyleSheets(): string {
  return Array.from(document.styleSheets)
              .map(sheet => {
      try {
        if (!sheet.cssRules) return '';

        const cssText = Array.from(sheet.cssRules)
                              .map(rule => (rule.cssText.startsWith('body') ? '' : rule.cssText))
                              .filter(o => o)
                              .join('\n');

        return cssText ? `<style>${cssText}</style>` : '';
      } catch (e) {
        console.warn('Unable to access stylesheet:', sheet.href);
        return '';
      }
    })
              .join('');
}

// One monster block. Keep each monster intact across page breaks, but let small ones share a page
// (no forced break).
export function buildMonsterBlockHtml(contentInnerHtml: string, useMm2024: boolean): string {
  const className = 'print' + (useMm2024 ? ' mm2024' : '');
  const blockStyle = 'break-inside: avoid; page-break-inside: avoid';
  return `<div class='${className}' style='${blockStyle}'>${contentInnerHtml}</div>`;
}

// A small gap between consecutive monsters (the dashed rule is added on screen only, below).
export function buildSeparatorHtml(): string {
  return `<div class='monster-separator'></div>`;
}

// The full print document: collected stylesheets, screen-only aids, and the first monster block.
export function buildPrintDocumentHtml(contentInnerHtml: string, useMm2024: boolean, styleSheetsHtml: string): string {
  const monsterHtml = buildMonsterBlockHtml(contentInnerHtml, useMm2024);

  // Screen-only aids: a dashed rule between monsters, plus faint horizontal lines marking
  // where the printed page breaks would fall. The interval is approximate — US Letter at
  // 96 CSS px/in = 1056px; @media screen keeps both out of the actual printout.
  const pageHeightPx = 1056;
  const extraStyles = `<style>
          .monster-separator { height: 0; margin: 22px 0; }
          @media screen {
            .monster-separator { border-top: 1px dashed #aaa; }
            body {
              background-image: repeating-linear-gradient(to bottom,
                transparent 0, transparent ${pageHeightPx - 1}px,
                rgba(200,0,0,0.35) ${pageHeightPx - 1}px, rgba(200,0,0,0.35) ${pageHeightPx}px);
            }
          }
        </style>`;

  return `
          <html>
            <head>${styleSheetsHtml}${extraStyles}</head>
            <body>
              ${monsterHtml}
            </body>
          </html>
        `;
}
